/**
 * 管理员视角的导演项目列表（含已归档）
 */

import { useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import toast from '../../Toast';
import { directorAdminApi, type AdminDirectorProject } from '../../../apps/director/services/directorAdminApi';
import { formatBeijingDateTime } from '../../../utils/time';

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'draft', label: '草稿' },
  { value: 'producing', label: '制作中' },
  { value: 'ready', label: '待合成' },
  { value: 'composing', label: '合成中' },
  { value: 'completed', label: '已完成' },
  { value: 'archived', label: '已归档' },
];

export default function DirectorProjectsTab() {
  const [data, setData] = useState<AdminDirectorProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const result = await directorAdminApi.listAllProjects({
        page,
        pageSize,
        status: statusFilter || undefined,
        userId: userIdFilter.trim() || undefined,
      });
      setData(result.data);
      setTotal(result.pagination.total);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const handleDelete = async (id: string, userName: string | null) => {
    if (!window.confirm(`确定要彻底删除用户 ${userName ?? ''} 的项目吗？此操作不可逆。`)) return;
    try {
      await directorAdminApi.deleteProject(id);
      toast.success('已删除');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="input"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          value={userIdFilter}
          onChange={(e) => setUserIdFilter(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="按用户 ID 搜索"
          className="input flex-1 max-w-xs"
        />
        <button onClick={load} className="btn-secondary px-3 py-1.5">
          搜索
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left">
                <th className="px-4 py-2 font-medium">项目名</th>
                <th className="px-4 py-2 font-medium">用户</th>
                <th className="px-4 py-2 font-medium">状态</th>
                <th className="px-4 py-2 font-medium">分镜数</th>
                <th className="px-4 py-2 font-medium">更新时间</th>
                <th className="px-4 py-2 font-medium text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-light-muted">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    加载中
                  </td>
                </tr>
              )}
              {!loading && data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-light-muted">
                    暂无数据
                  </td>
                </tr>
              )}
              {data.map((p) => (
                <tr key={p.id} className="border-b">
                  <td className="px-4 py-2">
                    <div className="font-medium">{p.name}</div>
                    {p.description && (
                      <div className="text-xs text-light-muted line-clamp-1">{p.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div>{p.user.name ?? '匿名'}</div>
                    <div className="text-xs text-light-muted">{p.user.email}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-0.5 rounded bg-gray-100 text-xs">{p.status}</span>
                  </td>
                  <td className="px-4 py-2">{p.shotCount}</td>
                  <td className="px-4 py-2 text-xs">
                    {formatBeijingDateTime(p.updatedAt)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleDelete(p.id, p.user.name)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded"
                      title="彻底删除"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="divide-y lg:hidden">
          {loading ? (
            <div className="px-4 py-8 text-center text-light-muted">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              加载中
            </div>
          ) : data.length === 0 ? (
            <div className="px-4 py-8 text-center text-light-muted">暂无数据</div>
          ) : (
            data.map((p) => (
              <div key={p.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-light-text">{p.name}</div>
                    {p.description && (
                      <div className="mt-1 text-xs text-light-muted line-clamp-2">{p.description}</div>
                    )}
                  </div>
                  <span className="px-2 py-0.5 rounded bg-gray-100 text-xs">{p.status}</span>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <InfoBlock label="用户" value={`${p.user.name ?? '匿名'} / ${p.user.email}`} />
                  <InfoBlock label="分镜数" value={String(p.shotCount)} />
                </div>
                <div className="text-xs text-light-muted">
                  更新时间：{formatBeijingDateTime(p.updatedAt)}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => handleDelete(p.id, p.user.name)}
                    className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                    title="彻底删除"
                  >
                    <Trash2 className="w-4 h-4" />
                    删除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-light-muted">共 {total} 条</span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary px-3 py-1 disabled:opacity-50"
          >
            上一页
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary px-3 py-1 disabled:opacity-50"
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 break-all text-sm text-light-text">{value}</div>
    </div>
  );
}
